// SEO Component for Gift-Wish - Dynamic meta tags for multi-language support
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts';

// SEO translations for all languages
const seoTranslations = {
  ru: {
    home: {
      title: "Gift-Wish — Создавайте списки подарков и делитесь с друзьями",
      description: "Бесплатный сервис для создания вишлистов на день рождения, свадьбу, Новый год. Друзья бронируют подарки — никаких дубликатов!",
      keywords: "список подарков, вишлист, день рождения, свадьба, новый год, подарки, что подарить"
    },
    privacy: {
      title: "Политика конфиденциальности — Gift-Wish",
      description: "Политика конфиденциальности Gift-Wish. Как мы защищаем ваши данные согласно GDPR."
    },
    terms: {
      title: "Условия использования — Gift-Wish",
      description: "Условия использования сервиса Gift-Wish для создания списков подарков."
    },
    contact: {
      title: "Контакты — Gift-Wish",
      description: "Свяжитесь с нами. Техподдержка и вопросы по сервису Gift-Wish."
    },
    dashboard: {
      title: "Мои списки — Gift-Wish",
      description: "Управляйте своими списками подарков на Gift-Wish."
    },
    wishlist: {
      title: "Список подарков — Gift-Wish",
      description: "Посмотрите список подарков и забронируйте подарок для друга."
    }
  },
  uk: {
    home: {
      title: "Gift-Wish — Створюйте списки подарунків та діліться з друзями",
      description: "Безкоштовний сервіс для створення вішлістів на день народження, весілля, Новий рік. Друзі бронюють подарунки — жодних дублікатів!",
      keywords: "список подарунків, вішліст, день народження, весілля, новий рік, подарунки, що подарувати"
    },
    privacy: {
      title: "Політика конфіденційності — Gift-Wish",
      description: "Політика конфіденційності Gift-Wish. Як ми захищаємо ваші дані згідно з GDPR."
    },
    terms: {
      title: "Умови використання — Gift-Wish",
      description: "Умови використання сервісу Gift-Wish для створення списків подарунків."
    },
    contact: {
      title: "Контакти — Gift-Wish",
      description: "Зв'яжіться з нами. Техпідтримка та питання щодо сервісу Gift-Wish."
    },
    dashboard: {
      title: "Мої списки — Gift-Wish",
      description: "Керуйте своїми списками подарунків на Gift-Wish."
    },
    wishlist: {
      title: "Список подарунків — Gift-Wish",
      description: "Перегляньте список подарунків та забронюйте подарунок для друга."
    }
  },
  en: {
    home: {
      title: "Gift-Wish — Create & Share Gift Wishlists",
      description: "Free wishlist service for birthdays, weddings, Christmas. Friends reserve gifts — no duplicates! Share your list and get exactly what you want.",
      keywords: "wishlist, gift list, birthday gifts, wedding registry, christmas wishlist, gift ideas, what to give"
    },
    privacy: {
      title: "Privacy Policy — Gift-Wish",
      description: "Gift-Wish Privacy Policy. How we protect your data according to GDPR."
    },
    terms: {
      title: "Terms of Service — Gift-Wish",
      description: "Terms of Service for Gift-Wish wishlist service."
    },
    contact: {
      title: "Contact Us — Gift-Wish",
      description: "Contact Gift-Wish support team. Questions and feedback welcome."
    },
    dashboard: {
      title: "My Wishlists — Gift-Wish",
      description: "Manage your gift wishlists on Gift-Wish."
    },
    wishlist: {
      title: "Gift Wishlist — Gift-Wish",
      description: "View the wishlist and reserve a gift for your friend."
    }
  },
  de: {
    home: {
      title: "Gift-Wish — Wunschlisten erstellen und teilen",
      description: "Kostenloser Wunschlisten-Service für Geburtstage, Hochzeiten, Weihnachten. Freunde reservieren Geschenke — keine Duplikate!",
      keywords: "Wunschliste, Geschenkliste, Geburtstagsgeschenke, Hochzeitsregistry, Weihnachtswunschliste"
    },
    privacy: {
      title: "Datenschutzrichtlinie — Gift-Wish",
      description: "Gift-Wish Datenschutzrichtlinie. Wie wir Ihre Daten gemäß DSGVO schützen."
    },
    terms: {
      title: "Nutzungsbedingungen — Gift-Wish",
      description: "Nutzungsbedingungen für den Gift-Wish Wunschlisten-Service."
    },
    contact: {
      title: "Kontakt — Gift-Wish",
      description: "Kontaktieren Sie das Gift-Wish Support-Team."
    },
    dashboard: {
      title: "Meine Wunschlisten — Gift-Wish",
      description: "Verwalten Sie Ihre Wunschlisten auf Gift-Wish."
    },
    wishlist: {
      title: "Geschenk-Wunschliste — Gift-Wish",
      description: "Sehen Sie die Wunschliste und reservieren Sie ein Geschenk."
    }
  },
  fr: {
    home: {
      title: "Gift-Wish — Créez et partagez vos listes de cadeaux",
      description: "Service gratuit de listes de souhaits pour anniversaires, mariages, Noël. Les amis réservent les cadeaux — pas de doublons!",
      keywords: "liste de souhaits, liste de cadeaux, cadeaux anniversaire, liste de mariage, liste de Noël"
    },
    privacy: {
      title: "Politique de confidentialité — Gift-Wish",
      description: "Politique de confidentialité de Gift-Wish. Comment nous protégeons vos données selon le RGPD."
    },
    terms: {
      title: "Conditions d'utilisation — Gift-Wish",
      description: "Conditions d'utilisation du service Gift-Wish."
    },
    contact: {
      title: "Contact — Gift-Wish",
      description: "Contactez l'équipe de support Gift-Wish."
    },
    dashboard: {
      title: "Mes listes — Gift-Wish",
      description: "Gérez vos listes de cadeaux sur Gift-Wish."
    },
    wishlist: {
      title: "Liste de cadeaux — Gift-Wish",
      description: "Consultez la liste et réservez un cadeau pour votre ami."
    }
  },
  es: {
    home: {
      title: "Gift-Wish — Crea y comparte listas de regalos",
      description: "Servicio gratuito de listas de deseos para cumpleaños, bodas, Navidad. Los amigos reservan regalos — ¡sin duplicados!",
      keywords: "lista de deseos, lista de regalos, regalos de cumpleaños, lista de boda, lista de Navidad"
    },
    privacy: {
      title: "Política de privacidad — Gift-Wish",
      description: "Política de privacidad de Gift-Wish. Cómo protegemos tus datos según el RGPD."
    },
    terms: {
      title: "Términos de servicio — Gift-Wish",
      description: "Términos de servicio de Gift-Wish."
    },
    contact: {
      title: "Contacto — Gift-Wish",
      description: "Contacta con el equipo de soporte de Gift-Wish."
    },
    dashboard: {
      title: "Mis listas — Gift-Wish",
      description: "Administra tus listas de regalos en Gift-Wish."
    },
    wishlist: {
      title: "Lista de regalos — Gift-Wish",
      description: "Mira la lista y reserva un regalo para tu amigo."
    }
  },
  cs: {
    home: {
      title: "Gift-Wish — Vytvořte a sdílejte seznamy přání",
      description: "Bezplatná služba pro seznamy přání k narozeninám, svatbám, Vánocům. Přátelé rezervují dárky — žádné duplicity!",
      keywords: "seznam přání, dárkový seznam, narozeninové dárky, svatební seznam, vánoční přání"
    },
    privacy: {
      title: "Zásady ochrany osobních údajů — Gift-Wish",
      description: "Zásady ochrany osobních údajů Gift-Wish. Jak chráníme vaše data podle GDPR."
    },
    terms: {
      title: "Podmínky služby — Gift-Wish",
      description: "Podmínky služby Gift-Wish."
    },
    contact: {
      title: "Kontakt — Gift-Wish",
      description: "Kontaktujte tým podpory Gift-Wish."
    },
    dashboard: {
      title: "Moje seznamy — Gift-Wish",
      description: "Spravujte své seznamy přání na Gift-Wish."
    },
    wishlist: {
      title: "Seznam dárků — Gift-Wish",
      description: "Prohlédněte si seznam a zarezervujte dárek."
    }
  },
  sk: {
    home: {
      title: "Gift-Wish — Vytvorte a zdieľajte zoznamy prianí",
      description: "Bezplatná služba pre zoznamy prianí k narodeninám, svadbe, Vianociam. Priatelia rezervujú darčeky — žiadne duplicity!",
      keywords: "zoznam prianí, darčekový zoznam, narodeninové darčeky, svadobný zoznam, vianočné priania"
    },
    privacy: {
      title: "Zásady ochrany osobných údajov — Gift-Wish",
      description: "Zásady ochrany osobných údajov Gift-Wish. Ako chránime vaše údaje podľa GDPR."
    },
    terms: {
      title: "Podmienky služby — Gift-Wish",
      description: "Podmienky služby Gift-Wish."
    },
    contact: {
      title: "Kontakt — Gift-Wish",
      description: "Kontaktujte tím podpory Gift-Wish."
    },
    dashboard: {
      title: "Moje zoznamy — Gift-Wish",
      description: "Spravujte svoje zoznamy prianí na Gift-Wish."
    },
    wishlist: {
      title: "Zoznam darčekov — Gift-Wish",
      description: "Pozrite si zoznam a zarezervujte darček."
    }
  }
};

// Get page key from pathname
const getPageKey = (pathname) => {
  if (pathname === '/') return 'home';
  if (pathname === '/privacy') return 'privacy';
  if (pathname === '/terms') return 'terms';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/dashboard') return 'dashboard';
  if (pathname.startsWith('/wishlist/')) return 'wishlist';
  if (pathname.startsWith('/event/')) return 'wishlist';
  return 'home';
};

// SEO Component - updates document head based on current page and language
export const SEO = ({ customTitle, customDescription, customImage, eventTitle, eventType }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const pageKey = getPageKey(location.pathname);
  
  useEffect(() => {
    const seo = seoTranslations[language] || seoTranslations.en;
    const pageSeo = seo[pageKey] || seo.home;
    
    // Use custom title for wishlists/events
    let title = customTitle || pageSeo.title;
    let description = customDescription || pageSeo.description;
    
    // For wishlist pages with event data
    if (eventTitle && pageKey === 'wishlist') {
      title = `${eventTitle} — Gift-Wish`;
      description = customDescription || pageSeo.description;
    }
    
    // Update document title
    document.title = title;
    
    // Update meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };
    
    // Basic meta
    updateMeta('description', description);
    updateMeta('title', title);
    if (pageSeo.keywords) {
      updateMeta('keywords', pageSeo.keywords);
    }
    
    // Open Graph
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', `https://gift-wish.eu${location.pathname}`, true);
    if (customImage) {
      updateMeta('og:image', customImage, true);
    }
    
    // Twitter
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    if (customImage) {
      updateMeta('twitter:image', customImage);
    }
    
    // Update html lang attribute
    document.documentElement.lang = language;
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = `https://gift-wish.eu${location.pathname}`;
    }
    
  }, [language, location.pathname, customTitle, customDescription, customImage, eventTitle, pageKey]);
  
  return null; // This component doesn't render anything
};

// Hook for setting page-specific SEO from components
export const usePageSEO = ({ title, description, image } = {}) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    if (title) {
      document.title = title;
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.content = title;
    }
    
    if (description) {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.content = description;
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.content = description;
    }
    
    if (image) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.content = image;
    }
    
  }, [title, description, image, language]);
};

export default SEO;
